import { Link } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import BalanceCard from "../components/billing/BalanceCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { billingApi } from "../api/billing";
import { endpointsApi } from "../api/endpoints";
import { formatDate } from "../utils/format";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StacuityData = any;

export default function Dashboard() {
  const balance = useApi(() => billingApi.getBalance(), []);
  const transactions = useApi(() => billingApi.getTransactions(), []);
  const endpoints = useApi(() => endpointsApi.list(), []);
  const payments = useApi(() => billingApi.getPayments(5), []);

  if (balance.loading && endpoints.loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const balanceData = balance.data?.data as StacuityData;
  const recentTransactions = (transactions.data?.data as StacuityData[])?.slice(0, 5) ?? [];
  const endpointList = endpoints.data?.data ?? [];
  const recentPayments = payments.data?.data ?? [];

  const activeEndpoints = endpointList.filter(
    (e) => e.status === "ENABLED" || e.status === "ONLINE",
  ).length;

  return (
    <PageContainer
      title="Dashboard"
      description="Account overview and recent activity"
    >
      <div className="space-y-6">
        {/* Balance card — API returns normalized pence values */}
        {balanceData && (
          <BalanceCard
            balance={balanceData.balance ?? 0}
            currency={balanceData.currency ?? "GBP"}
            alertThreshold={balanceData.alertThreshold}
          />
        )}
        {balance.error && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-700">
              Unable to load balance: {balance.error}
            </p>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total Endpoints"
            value={String(endpointList.length)}
          />
          <StatCard label="Active" value={String(activeEndpoints)} />
          <StatCard
            label="Recent Top-ups"
            value={String(recentPayments.length)}
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent transactions */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-medium text-gray-900">
                Recent Transactions
              </h3>
              <Link
                to="/usage"
                className="text-xs text-simsy-600 hover:text-simsy-700"
              >
                View all
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {recentTransactions.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">
                  No recent transactions
                </p>
              ) : (
                recentTransactions.map((tx: StacuityData) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div>
                      <p className="text-sm text-gray-900">{tx.description}</p>
                      <p className="text-xs text-gray-500">
                        {formatDate(tx.transactionTime ?? tx.timestamp)}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {tx.formattedAmount ?? `£${Math.abs(tx.amount ?? 0).toFixed(2)}`}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Endpoints summary */}
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-medium text-gray-900">
                Endpoints
              </h3>
              <Link
                to="/endpoints"
                className="text-xs text-simsy-600 hover:text-simsy-700"
              >
                View all
              </Link>
            </div>
            <div className="divide-y divide-gray-100">
              {endpointList.length === 0 ? (
                <p className="p-4 text-center text-sm text-gray-500">
                  No endpoints configured
                </p>
              ) : (
                endpointList.slice(0, 5).map((ep) => (
                  <Link
                    key={ep.id}
                    to={`/endpoints/${ep.id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                  >
                    <div>
                      <p className="text-sm text-gray-900">
                        {ep.name || ep.iccid}
                      </p>
                      <p className="text-xs text-gray-500">{ep.iccid}</p>
                    </div>
                    <StatusBadge status={ep.status} />
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isActive =
    status === "ENABLED" || status === "ONLINE" || status === "ACTIVE";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      <span
        className={`mr-1.5 h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-green-500" : "bg-gray-400"
        }`}
      />
      {status}
    </span>
  );
}
